import { useState, useCallback, useEffect, useRef, createContext, useContext } from "react";
import "./Toast.css";
import { ChevronDownIcon } from "lucide-react";
import useSelection from "antd/es/table/hooks/useSelection";
import { useSelector } from "react-redux";

const V = {
  success: {
    accent: "#16a34a", iconBg: "#dcfce7", iconFg: "#15803d",
    dark: { iconBg: "#0d2e1a", iconFg: "#4ade80" },
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
  },
  error: {
    accent: "#dc2626", iconBg: "#fee2e2", iconFg: "#b91c1c",
    dark: { iconBg: "#2d0f0f", iconFg: "#f87171" },
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="15" height="15">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ),
  },
  warning: {
    accent: "#d97706", iconBg: "#fef3c7", iconFg: "#b45309",
    dark: { iconBg: "#2d1f06", iconFg: "#fbbf24" },
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
  info: {
    accent: "#2563eb", iconBg: "#dbeafe", iconFg: "#1d4ed8",
    dark: { iconBg: "#0c1e3d", iconFg: "#60a5fa" },
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    ),
  },
};

// ─── SVG icons ────────────────────────────────────────────────────────────────
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="11" height="11">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);


// Constants 
const AUTO_MS    = 5000; 
const PEEK       = 5;    
const SCALE_STEP = 0.02; 
const MAX_GHOSTS = 3;
const ANIM_OUT_MS = 320;

// Toast card 
const Toast = ({ id, type, message, onRemove, paused, leaving }) => {
  const isDark = useSelector((state) => state.chart?.isDark ?? false);
  const vt = V[type] ?? V.info;

  const iconBg = isDark ? vt.dark?.iconBg : vt.iconBg;
  const iconFg = isDark ? vt.dark?.iconFg : vt.iconFg;

  return (
    <div
      className={`toast-card${leaving ? " is-leaving" : ""}`}
      style={{ borderLeftColor: vt.accent }}
    >
      <div
        className="toast-progress"
        style={{
          background: vt.accent,
          animationPlayState: paused ? "paused" : "running",
          "--toast-duration": `${AUTO_MS}ms`,
        }}
      />

      <div className="toast-icon" style={{ background: iconBg, color: iconFg }}>
        {vt.icon}
      </div>

      <div className="toast-body">
        <div className="toast-message">
          {message}
        </div>
      </div>

      <button className="toast-close" onClick={() => onRemove(id)} aria-label="Close">
        <CloseIcon />
      </button>
    </div>
  );
};

// Context
const ToastCtx = createContext(null);
let _uid = 1;

// Static toast object for use outside of components
export const toast = {
  success: (msg) => { if (toast._add) toast._add("success", msg); else console.warn("Toast not initialized", msg); },
  error:   (msg) => { if (toast._add) toast._add("error",   msg); else console.warn("Toast not initialized", msg); },
  warning: (msg) => { if (toast._add) toast._add("warning", msg); else console.warn("Toast not initialized", msg); },
  info:    (msg) => { if (toast._add) toast._add("info",    msg); else console.warn("Toast not initialized", msg); },
  _add: null,
  _removeAll: null,
};

export function ToastProvider({ children, position = "bottom-right" }) {
  const [toasts,     setToasts]     = useState([]);
  const [leavingIds, setLeavingIds] = useState(new Set());
  const [expanded,   setExpanded]   = useState(false);
  const [paused,     setPaused]     = useState(false);
  const timerRef = useRef(null);

  const toastsRef = useRef(toasts);
  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  const animateOut = useCallback((ids) => {
    const idSet = new Set(ids);
    setLeavingIds(prev => new Set([...prev, ...idSet]));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => !idSet.has(t.id)));
      setLeavingIds(prev => {
        const next = new Set(prev);
        idSet.forEach(id => next.delete(id));
        return next;
      });
    }, ANIM_OUT_MS);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Use ref to get current toasts at timeout execution
      const currentToasts = toastsRef.current;
      animateOut(currentToasts.map(t => t.id));
      setTimeout(() => setExpanded(false), ANIM_OUT_MS);
    }, AUTO_MS);
  }, [animateOut]);

  const prevLengthRef = useRef(0);
  useEffect(() => {
    if (toasts.length === 0) {
      clearTimeout(timerRef.current);
      setExpanded(false);
      prevLengthRef.current = 0;
      return;
    }
    
    if (toasts.length > prevLengthRef.current && !paused && !expanded) {
      scheduleClose();
    }
    prevLengthRef.current = toasts.length;
    
    return () => clearTimeout(timerRef.current);
  }, [toasts.length, paused, expanded, scheduleClose]);

  useEffect(() => {
    if (paused || expanded) {
      clearTimeout(timerRef.current);
    } else if (toasts.length > 0) {
      scheduleClose();
    }
  }, [paused, expanded, scheduleClose]);

  useEffect(() => {
    if (toasts.length <= 1) setExpanded(false);
  }, [toasts.length]);

  const add = useCallback((type, message) => {
    const id = _uid++;
    setToasts(prev => [{ id, type, message }, ...prev]);
    return id;
  }, []);

  const remove = useCallback((id) => {
    animateOut([id]);
  }, [animateOut]);

  const removeAll = useCallback(() => {
    clearTimeout(timerRef.current);
    setToasts(prev => {
      animateOut(prev.map(t => t.id));
      return prev;
    });
    setTimeout(() => {
      setExpanded(false);
      setPaused(false);
    }, ANIM_OUT_MS);
  }, [animateOut]);

  const api = useRef({
    success: msg => add("success", msg),
    error:   msg => add("error",   msg),
    warning: msg => add("warning", msg),
    info:    msg => add("info",    msg),
  });

  useEffect(() => {
    toast._add = add;
    toast._removeAll = removeAll;
    return () => {
      toast._add = null;
      toast._removeAll = null;
    };
  }, [add, removeAll]);

  const handleShowMore   = () => { setExpanded(true);  setPaused(true);  };
  const handleCollapse   = () => { setExpanded(false); setPaused(false); };
  const handleHoverEnter = () => setPaused(true);
  const handleHoverLeave = () => { if (!expanded) setPaused(false); };

  return (
    <ToastCtx.Provider value={api.current}>
      {children}
      <ToastStack
        toasts={toasts}
        leavingIds={leavingIds}
        expanded={expanded}
        paused={paused || expanded}
        position={position}
        onRemove={remove}
        onRemoveAll={removeAll}
        onShowMore={handleShowMore}
        onCollapse={handleCollapse}
        onHoverEnter={handleHoverEnter}
        onHoverLeave={handleHoverLeave}
      />
    </ToastCtx.Provider>
  );
}


export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

function ToastStack({
  toasts, leavingIds, expanded, paused, position,
  onRemove, onRemoveAll, onShowMore, onCollapse,
  onHoverEnter, onHoverLeave,
}) {
  if (toasts.length === 0) return null;

  const hiddenCount = toasts.length - 1;
  const posClass    = `pos-${position}`;

  // COLLAPSED view 
  if (!expanded) {
    const ghostCount  = Math.min(hiddenCount, MAX_GHOSTS);

    return (
      <div
        className={`toast-root ${posClass}`}
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
      >
        {hiddenCount > 0 && (
          <div className="toast-controls">
            <button className="toast-pill-btn" onClick={onShowMore}>
              Show more
              <span className="toast-pill-badge">{hiddenCount}</span>
            </button>
            <button className="toast-icon-btn" onClick={onRemoveAll} aria-label="Dismiss all">
              <CloseIcon />
            </button>
          </div>
        )}

        <div className="toast-stack-wrap">
          {/* Ghost decorative layers */}
          {Array.from({ length: ghostCount }).map((_, gi) => {
            const depth = gi + 1;
            return (
              <div
                key={gi}
                className="toast-ghost"
                style={{
                  bottom:    -(depth * PEEK),
                  transform: `translateX(-50%) scaleX(${1 - depth * SCALE_STEP})`,
                  zIndex:    MAX_GHOSTS - depth + 1,
                }}
              />
            );
          })}

          <div className="toast-top-card-wrap">
            <Toast
              key={toasts[0].id}
              {...toasts[0]}
              onRemove={onRemove}
              paused={paused}
              leaving={leavingIds.has(toasts[0].id)}
            />
          </div>
        </div>
      </div>
    );
  }

  // EXPANDED view 
  return (
    <div
      className={`toast-root ${posClass}`}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
    >
      <div className="toast-controls">
        <button className="toast-pill-btn" onClick={onCollapse}>
          <ChevronDownIcon /> Collapse
        </button>
        <button className="toast-icon-btn" onClick={onRemoveAll} aria-label="Dismiss all">
          <CloseIcon />
        </button>
      </div>

      {/* Scrollable list */}
      <div className="toast-expanded-list">
        {toasts.map(t => (
          <Toast
            key={t.id}
            {...t}
            onRemove={onRemove}
            paused={true}
            leaving={leavingIds.has(t.id)}
          />
        ))}
      </div>
    </div>
  );
}